import { Row, Col, Modal, Form, DatePicker } from "antd";
import { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import { MAKE_URL } from "../../constants/conts";
import BrandSelect from "../../components/select/BrandSelect";
import CategorySelect from "../../components/select/CategorySelect";
import moment from "moment";
import SegmentSelect from "../../components/select/SegmentSelect";
import FileUpload from "../../components/FileUpload";

const From = ({ path, sectionName, show, hide, data, refresh }) => {

  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState([]);
  const [pdf, setPdf] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    if (!data) return;

    const { image_url, pdf_url, ...otherData } = data;
    const mappedImage = image_url ? [{ url: image_url }] : [];
    const mappedPdf = pdf_url ? [{ url: pdf_url }] : [];

    form.setFieldsValue({
      ...otherData,
      start_date: data.start_date ? moment(data.start_date, "YYYY-MM-DD") : null,
      end_date: data.end_date ? moment(data.end_date, "YYYY-MM-DD") : null,
      image: mappedImage,
      pdf: mappedPdf,
    });

    setSelectedBrand(data.brand_id || null);

    setImage(mappedImage);
    setPdf(mappedPdf);

  }, [data, form])

  const handleImage = (data) => {
    setImage(data);
    form.setFieldValue('image', data);
  }

  const handlePdf = (data) => {
    setPdf(data);
    form.setFieldValue('pdf', data);
  }

  const onCreate = async (values) => {
    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("brand_id", values.brand_id || "");
      formData.append("segment_id", values.segment_id || "");
      formData.append("category_id", values.category_id || "");
      formData.append("start_date", values.start_date ? values.start_date.format("YYYY-MM-DD") : "");
      formData.append("end_date", values.end_date ? values.end_date.format("YYYY-MM-DD") : "");

      const imageData = values.image;
      const pdfData = values.pdf;

      if (imageData) {
        if (imageData.originFileObj) {
          formData.append("image", imageData.originFileObj);
        } else if (imageData.url) {
          const response = await fetch(imageData.url);
          const blob = await response.blob();
          const fileName = imageData.name || imageData.url.split("/").pop() || "file";
          formData.append("image", blob, fileName);
        }
      }

      if (pdfData) {
        if (pdfData.originFileObj) {
          formData.append("pdf", pdfData.originFileObj);
        } else if (pdfData.url) {
          const response = await fetch(pdfData.url);
          const blob = await response.blob();
          const fileName = pdfData.name || pdfData.url.split("/").pop() || "file";
          formData.append("pdf", blob, fileName);
        }
      }

      request({
        url: MAKE_URL(path, data),
        method: 'POST',
        data: formData,
        onSuccess: (data) => {
          setLoading(false);
          if (data.status) {
            ShowToast(data.message, Severty.SUCCESS);
            hide();
            refresh();
          } else {
            ShowToast(data.message, Severty.ERROR);
          }
        },
        onError: (error) => {
          setLoading(false);
          ShowToast(error.response.data.message, Severty.ERROR);
        },
      });
    } catch (error) {
      setLoading(false);
      ShowToast("Error processing file", Severty.ERROR);
    }
  };

  return (
    <Modal
      open={show}
      title={`${data ? 'Update ' + sectionName : 'Create a New ' + sectionName}`}
      okText="Ok"
      onCancel={hide}
      width={900}
      okButtonProps={{
        form: 'create',
        htmlType: 'submit',
        loading: loading,
      }}
    >
      <Form id="create" form={form} onFinish={onCreate} layout="vertical">

        <Row gutter={[16, 0]}>

          <Col span={24} xs={12} sm={12} md={12}>
            <Form.Item
              name="brand_id"
              label="Select Brand"
              rules={[{ required: true, message: "Missing Brand Selection" }]}
            >
              <BrandSelect
                value={selectedBrand}
                onChange={(val) => {
                  form.setFieldsValue({
                    brand_id: val,
                    category_id: null,
                    segment_id: null,
                  });
                  setSelectedBrand(val);
                }}
              />
            </Form.Item>
          </Col>

          <Col span={24} xs={12} sm={12} md={12}>
            <Form.Item
              name="category_id"
              label="Select Category"
              rules={[{ required: true, message: "Missing Category Selection" }]}
            >
              <CategorySelect
                brandId={selectedBrand}
                disabled={!selectedBrand}
                shouldFetch={!!selectedBrand}
                value={form.getFieldValue("category_id")}
                onChange={(val) => form.setFieldValue("category_id", val)}
              />
            </Form.Item>
          </Col>

          <Col span={24} xs={12} sm={12} md={12}>
            <Form.Item
              label="Select Segments"
              name="segment_id"
              required
              rules={[{ required: true, message: "Please select segment!" }]}
            >
              <SegmentSelect
                brandId={selectedBrand}
                disabled={!selectedBrand}
                shouldFetch={!!selectedBrand}
                value={form.getFieldValue("segment_id")}
                onChange={(val) => form.setFieldValue("segment_id", val)}
              />
            </Form.Item>
          </Col>

          <Col span={24} xs={12} sm={12} md={6}>
            <Form.Item
              name="start_date"
              label="Start Date"
              rules={[{ required: true, message: "Missing Start Date Selection" }]}
            >
              <DatePicker format="YYYY-MM-DD" placeholder="Select start date" />
            </Form.Item>
          </Col>

          <Col span={24} xs={12} sm={12} md={6}>
            <Form.Item
              name="end_date"
              label="End Date"
              dependencies={["start_date"]}
              rules={[
                { required: true, message: "Missing End Date Selection" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("start_date");
                    if (!value || !startDate || value.isAfter(startDate)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("End date must be after start date")
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                placeholder="Select end date"
                disabledDate={(current) => {
                  const startDate = form.getFieldValue("start_date");
                  return startDate && current.isBefore(startDate, "day");
                }}
              />
            </Form.Item>
          </Col>

          <Col span={24} xs={24} sm={24} md={12}>
            <Form.Item
              label="Upload Image (1:1 Ratio)"
              valuePropName="value"
              name="image"
            // rules={[{ required: true, message: "Please upload image!" }]}
            >
              <FileUpload filterType={["image"]} btnName={'Image'} aspectRatio={1} onChange={(data) => handleImage(data)} />
            </Form.Item>
          </Col>

          <Col span={24} xs={24} sm={24} md={12}>
            <Form.Item
              label="Upload PDF/Document"
              valuePropName="value"
              name="pdf"
              rules={[{ required: true, message: "Please upload PDF!" }]}
            >
              <FileUpload filterType={["image", "file"]} btnName={'PDF'} onChange={(data) => handlePdf(data)} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
};

export default From;